import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import useQuery, { _get, _post } from "../../Utils/Helper";
import "./ViewLesson.css";

interface Lesson {
  id: number;
  title: string;
  subject: string;
  teacher: string;
  content: string;
  attachment?: string;
}

interface Comment {
  id: number;
  user_id: number;
  user_name: string;
  user_role: string;
  comment: string;
  created_at: string;
  updated_at: string | null;
  lesson_id: number;
  parent_id: number | null;
  replies?: Comment[];
}

const ViewLesson: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const query = useQuery();
  const lesson_id = query.get("lesson_id");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [lessonData, setLessonData] = useState<Lesson | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<number | null>(null);

  const fetchLesson = useCallback(() => {
    _get(`lessons?query_type=select&id=${lesson_id}`, (res) => {
      setLessonData(res.data[0]);
    });
  }, [lesson_id]);

  const fetchComments = useCallback(() => {
    _get(`commentLessons?query_type=select&lesson_id=${lesson_id}`, (res) => {
      setComments(res.data);
    });
  }, [lesson_id]);

  
  const addComment = (comment: string, parentId: number | null = null) => {
    const commentData = {
      lesson_id,
      comment,
      user_id: user.id,
      user_name: user.name,
      user_role: user.role,
      parent_id: parentId,
    };

    _post("commentLessons?query_type=create", commentData, () => {
      fetchComments();
      setNewComment("");
      setReplyingTo(null);
    });
  };

  useEffect(() => {
    fetchLesson();
    fetchComments();
  }, [fetchLesson, fetchComments]);

  const renderComment = (comment: Comment) => (
    <div
      key={comment.id}
      className={`comment-item ${comment?.parent_id ? "reply" : ""}`}
    >
      <div className="comment-header">
        <div className="user-avatar">{comment?.user_name[0].toUpperCase()}</div>
        <span className="user-name">{comment?.user_name}</span>
        <span className={`user-role ${comment?.user_role.toLowerCase()}`}>
          {comment?.user_role}
        </span>
        <span className="comment-timestamp">
          {new Date(comment?.created_at).toLocaleString()}
        </span>
      </div>
      <p className="comment-content">{comment?.comment}</p>
      <div className="comment-actions">
        {!comment?.parent_id && (
          <span
            className="comment-action"
            onClick={() => setReplyingTo(comment?.id)}
          >
            Reply
          </span>
        )}
      </div>
      {comment?.replies && comment?.replies.map(renderComment)}
    </div>
  );

  if (!lessonData) {
    return <div>Loading...</div>;
  }

  return (
    <div className="lesson-container">
      <div className="lesson-card">
        <h1 className="lesson-title">{lessonData.title}</h1>
        <div className="lesson-meta">
          <span className="lesson-subject">Subject: {lessonData.subject}</span>
          <span className="lesson-teacher">Teacher: {lessonData.teacher}</span>
        </div>
        {/* {JSON.stringify(user)} */}
        <p className="lesson-content">{lessonData.content}</p>
        {lessonData.attachment && (
          <a href={lessonData.attachment} className="attachment-button">
            View Attachment
          </a>
        )}
      </div>

      <div className="comments-section">
        <h2>Comments</h2>
        {comments.map(renderComment)}
      </div>

      <div className="comment-form">
        <textarea
          className="comment-input"
          placeholder={replyingTo ? "Write your reply..." : "Add a comment..."}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
        />
        <button
          className="submit-button"
          onClick={() => {
            if (newComment.trim()) {
              addComment(newComment, replyingTo);
            }
          }}
        >
          {replyingTo ? "Reply" : "Comment"}
        </button>
      </div>
    </div>
  );
};

export default ViewLesson;
